/**
 * Helper functions for row management (add/remove rows)
 */

/**
 * Add a manual organism row - inserts in correct group position (above SUM row)
 */
export const addManualRowHelper = (
  manualGroup,
  manualSpecies,
  manualDepth,
  currentLocations,
  setCustomOrganisms,
  setOrganisms,
  setGridData,
  setManualGroup,
  setManualSpecies,
  setManualDepth,
  setShowManualForm,
  toast,
  savePermanently = false,
  onSaveToTemplate = null
) => {
  
  if (!manualGroup.trim() || !manualSpecies.trim()) {
    toast.error('Fyll ut både ordre/gruppe og art');
    return;
  }
  
  const newOrganism = {
    group: manualGroup.trim(),
    species: manualSpecies.trim(),
    depth: manualDepth || '3',
    isSpecies: true,
    isCustom: true,
    isTemporary: !savePermanently // Mark as temporary if not saving permanently
  };
  
  
  // Add to custom organisms
  setCustomOrganisms(prev => {
    const updated = [...prev, newOrganism];
    return updated;
  });
  
  // Insert into organisms at the correct position (within its group, ABOVE SUM row)
  setOrganisms(prev => {
    const groupName = newOrganism.group;
    
    // Find the correct insertion point:
    // 1. Find where this group's organisms are
    // 2. Insert BEFORE any SUM row for this group
    let insertIndex = -1;
    let groupStartIndex = -1;
    let groupEndIndex = -1;
    
    for (let i = 0; i < prev.length; i++) {
      if (prev[i].group === groupName) {
        if (groupStartIndex === -1) {
          groupStartIndex = i;
        }
        groupEndIndex = i;
        
        // Check if this is the SUM row - if so, insert BEFORE it
        const species = prev[i].species?.toUpperCase() || '';
        if (species.includes('SUM') || species === groupName.toUpperCase() + ' SUM') {
          insertIndex = i; // Insert before SUM
          break;
        }
      }
    }
    
    // If we found a group but no SUM row, insert at the end of the group
    if (insertIndex === -1 && groupEndIndex !== -1) {
      insertIndex = groupEndIndex + 1;
    }
    
    // If no organisms of this group exist, find where to insert based on group order
    if (insertIndex === -1) {
      const groupOrder = [...new Set(prev.map(o => o.group))];
      const sortedGroups = [...groupOrder, groupName].sort();
      const newGroupIndex = sortedGroups.indexOf(groupName);
      
      if (newGroupIndex === 0) {
        insertIndex = 0;
      } else {
        // Find the end of the previous group
        const prevGroup = sortedGroups[newGroupIndex - 1];
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i].group === prevGroup) {
            insertIndex = i + 1;
            break;
          }
        }
      }
      
      // Fallback to end if still not found
      if (insertIndex === -1) {
        insertIndex = prev.length;
      }
    }
    
    
    // Insert at the correct position
    const updated = [...prev];
    updated.splice(insertIndex, 0, newOrganism);
    return updated;
  });
  
  // Initialize grid data for new organism using currentLocations
  const organismKey = `${newOrganism.group}_${newOrganism.species}`;
  setGridData(prev => ({
    ...prev,
    [organismKey]: currentLocations.reduce((acc, loc) => ({ ...acc, [loc]: '' }), {})
  }));
  
  // Save combination for future use (local storage)
  const savedCombinations = JSON.parse(localStorage.getItem('algae_combinations') || '{}');
  if (!savedCombinations[manualGroup.trim()]) {
    savedCombinations[manualGroup.trim()] = [];
  }
  if (!savedCombinations[manualGroup.trim()].includes(manualSpecies.trim())) {
    savedCombinations[manualGroup.trim()].push(manualSpecies.trim());
    localStorage.setItem('algae_combinations', JSON.stringify(savedCombinations));
  }
  
  // If saving permanently to template, call the callback
  if (savePermanently && onSaveToTemplate) {
    onSaveToTemplate(newOrganism);
  }
  
  // Reset form
  setManualGroup('');
  setManualSpecies('');
  setManualDepth('');
  setShowManualForm(false);
  
  const permanentText = savePermanently ? ' (lagret i skjema)' : ' (kun denne gang)';
  toast.success(`"${newOrganism.species}" lagt til under ${newOrganism.group}${permanentText}`);
};

/**
 * Remove a custom organism row
 */
export const removeCustomRowHelper = (
  organismKey,
  customOrganisms,
  setCustomOrganisms,
  gridData,
  setGridData,
  calculateSums,
  toast
) => {
  
  // Remove from customOrganisms
  setCustomOrganisms(prev => 
    prev.filter(org => `${org.group}_${org.species}` !== organismKey)
  );
  
  // Remove from gridData
  setGridData(prev => {
    const updated = { ...prev };
    delete updated[organismKey];
    return calculateSums(updated);
  });
  
  toast.success('Rad fjernet');
};

/**
 * Remove a zooplankton row
 */
export const removeZooplanktonRowHelper = (
  organismKey,
  currentZooplanktonOrganisms,
  setCurrentZooplanktonOrganisms,
  customZooplanktonOrganisms,
  setCustomZooplanktonOrganisms,
  zooplanktonGridData,
  setZooplanktonGridData,
  toast
) => {
  
  // Remove from currentZooplanktonOrganisms
  setCurrentZooplanktonOrganisms(prev => {
    const filtered = prev.filter(org => `${org.sampleType}_${org.organism}` !== organismKey);
    return filtered;
  });
  
  // Also remove from customZooplanktonOrganisms if it's a custom one
  setCustomZooplanktonOrganisms(prev => 
    prev.filter(org => `${org.sampleType}_${org.organism}` !== organismKey)
  );
  
  // Remove from grid data
  setZooplanktonGridData(prev => {
    const updated = { ...prev };
    delete updated[organismKey];
    return updated;
  });
  
  toast.success('Zooplankton-rad fjernet');
};

/**
 * Remove a row (handles both custom and standard organisms)
 * Shows a dialog asking: one-time only, save in template, or save as new template
 */
export const removeRowHelper = async (
  organismKey,
  organism,
  removeCustomRow,
  setOrganisms,
  setGridData,
  calculateSums,
  setDepthOverrides,
  toast,
  isDemo = false,
  confirmDialog = null,
  onSaveTemplateWithDeletion = null,
  templateName = null
) => {
  // Check if it's a custom organism
  if (organism.isCustom) {
    removeCustomRow(organismKey);
    return;
  }
  
  // For standard organisms, show a dialog with options
  let choice = 'one-time'; // default
  
  if (confirmDialog) {
    const saveLabel = templateName ? `Lagre i mal (${templateName})` : 'Lagre i mal';
    const result = await confirmDialog({
      title: `Fjern "${organism.species}"?`,
      message: `Korleis vil du fjerne "${organism.group} - ${organism.species}" frå tabellen?`,
      confirmText: 'Berre denne gongen',
      cancelText: 'Avbryt',
      variant: 'warning',
      extraActions: [
        { label: saveLabel, value: 'save-template', variant: 'default' }
      ]
    });
    
    if (result === false) return; // Cancelled
    if (result === 'save-template') choice = 'save-template';
  } else {
    if (!window.confirm(`Fjern "${organism.group} - ${organism.species}" frå skjemaet?`)) return;
  }
  
  // Remove from organisms state
  setOrganisms(prev => prev.filter(org => 
    `${org.group}_${org.species}` !== organismKey
  ));
  
  // Remove data
  setGridData(prev => {
    const updated = { ...prev };
    delete updated[organismKey];
    return calculateSums(updated);
  });
  
  // Remove depth override if exists
  setDepthOverrides(prev => {
    const updated = { ...prev };
    delete updated[organismKey];
    return updated;
  });
  
  // If user chose to save in template, trigger template update
  if (choice === 'save-template' && onSaveTemplateWithDeletion) {
    onSaveTemplateWithDeletion(organismKey, organism);
  }
  
  // IMPORTANT: Also remove from localStorage (demo mode) so it doesn't come back
  if (isDemo) {
    const savedAlgae = localStorage.getItem('demo_organisms_algae');
    if (savedAlgae) {
      const algaeStructure = JSON.parse(savedAlgae);
      const group = organism.group;
      const species = organism.species;
      if (algaeStructure[group]) {
        algaeStructure[group] = algaeStructure[group].filter(
          sp => sp.species !== species
        );
      }
      localStorage.setItem('demo_organisms_algae', JSON.stringify(algaeStructure));
    }
  }
  
  toast.success(choice === 'save-template' ? 'Rad fjerna og mal oppdatert' : 'Rad fjerna (berre denne gongen)');
};

/**
 * Handle right-click on zooplankton row
 */
export const handleZooplanktonRowRightClickHelper = (
  e,
  organismKey,
  organism,
  setContextMenu
) => {
  e.preventDefault();
  e.stopPropagation();
  
  // Calculate menu position with viewport boundary checks
  const menuWidth = 200;
  const menuHeight = 100;
  
  let x = e.clientX;
  let y = e.clientY;
  
  if (x + menuWidth > window.innerWidth) {
    x = window.innerWidth - menuWidth - 10;
  }
  
  if (y + menuHeight > window.innerHeight) {
    y = window.innerHeight - menuHeight - 10;
  }
  
  x = Math.max(10, x);
  y = Math.max(10, y);
  
  setContextMenu({
    x,
    y,
    organismKey,
    organism,
    isRowContext: true,
    isZooplankton: true
  });
};
